import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  PRIMARY_OUTLET,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';

import { AuthService } from '../services/auth.service';

/**
 * Resolves `/client/current/<path>` to `/client/<CompanyId>/<path>` for the
 * signed-in client.
 *
 * Every client page here is keyed by the client's CompanyId, but a link from
 * outside this app (a Chameleon experience, the client app) is static and cannot
 * carry one. The literal `current` segment stands in for it. A staff member
 * viewing as a client resolves to that client: while impersonating, the session
 * principal is the client.
 *
 * Attached ONLY to the `client/current/**` route, below the `client` route's
 * AuthGuard, so a signed-out visitor never reaches it — they go to login with
 * the placeholder URL as `returnUrl`, and it resolves after they sign in.
 */
@Injectable({
  providedIn: 'root'
})
export class ClientCompanyPlaceholderGuard {
  public constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  public canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): UrlTree {
    // Staff (who carry a CompanyId of their own) and vendors have no client
    // company to put here. `/` is the login page, which sends a signed-in user
    // to their own home.
    if (!this.authService.isClient()) {
      return this.router.parseUrl('/');
    }

    // Edit the parsed URL rather than rebuilding it from strings, so the query
    // string, the fragment and matrix params on later segments survive.
    const url = this.router.parseUrl(state.url);
    const segments = url.root.children[PRIMARY_OUTLET].segments;
    segments[1] = new UrlSegment(
      String(this.authService.getUserCompanyId()),
      {}
    );
    if (segments.length === 2) {
      segments.push(new UrlSegment('dashboard', {}));
    }

    // Returned as a plain UrlTree: the router's own redirect handling already
    // keeps the placeholder out of history. Forcing `replaceUrl` would instead
    // overwrite the previous page's entry on an in-app navigation.
    return url;
  }
}
